/**
 * Navigation paths configuration.
 * This file contains only static navigation data with no dependencies to avoid circular imports.
 *
 * IMPORTANT: Do not import hooks or providers in this file to prevent circular dependencies.
 */

import type { DeviceType } from '@talos/kyoko';
import { ACTION, IconName, MixpanelEvent } from '@talos/kyoko';
import type { RouteObject } from 'react-router-dom';
import type { FeatureFlags } from '../hooks/useFeatureFlag';
import { isLpSettlementLegacyVisible } from '../utils/isLpSettlementLegacyVisible';

export type NavigationItem = RouteObject & {
  icon?: IconName;
  /** Nav Display Label; Label is undefined for structural routes that are not displayed in the navigation UI */
  label: string | undefined;
  /** Short label shown in the Nav Sidebar */
  shortLabel?: string;
  /** Optional count shown by mobile tab bars. */
  indicatorCount?: number;
  /** Checks visibility of the item. Receives feature flags and authorization check function. */
  isVisible?: (flags: FeatureFlags, isAuthorized: (permission: ACTION) => boolean) => boolean;
  children?: NavigationItem[];
  /** The position or behavior of the navigation items in the navigation UI
   * - top: displayed in the top navigation bar
   * - bottom: displayed in the bottom navigation bar
   * - user-menu: menu items displayed as a popout from the user menu
   */
  position?: 'top' | 'bottom' | 'user-menu';
  /** Whether to enable spaces for this navigation item */
  enableSpaces?: boolean;
  /** Keep this navigation item visible even when all children are filtered out. */
  keepWhenEmpty?: boolean;
  /** Mixpanel event triggered when the navigation item is clicked */
  mixpanelEvent?: MixpanelEvent;
  /** When set on a child (e.g. mobile market tabs), marks the dockview-active panel for default redirects */
  isActive?: boolean;
  /**
   * When set, clicking this item in the mobile tab bar navigates here instead of `path`.
   * `path` is still used for route matching (determining whether the tab is active).
   * Mirrors `TabBarItem.navigationPath` in kyoko.
   */
  navigationPath?: string;
  /** Whether the mobile sub-tab actions sheet should allow renaming this item. */
  isEditable?: boolean;
  /** Whether this navigation item can be removed, e.g. a custom space or mobile sub-tab. */
  isRemovable?: boolean;
  /** Confirmation copy shown before removing this navigation item. */
  removeConfirmationText?: string;
  /** The device type intended for the space the navigation item represents */
  deviceType?: DeviceType;
  /**
   * Whether rearrange mode allows dropping spaces into this group.
   * Merged fixed/space groups and space-only groups set this to true.
   */
  allowSpaceDrops?: boolean;
};

/** URL slug for the Analytics page under Trading; reserved so space groups cannot claim it. */
export const ANALYTICS_NAV_GROUP_SLUG = 'analytics';
export const ANALYTICS_NAV_GROUP_PATH = `/trading/${ANALYTICS_NAV_GROUP_SLUG}`;
export const TRADING_ANALYTICS_BASE_PATH = `${ANALYTICS_NAV_GROUP_PATH}/post-trade`;
export const LEGACY_ANALYTICS_BASE_PATH = '/analytics';
export type AnalyticsBasePath = typeof TRADING_ANALYTICS_BASE_PATH | typeof LEGACY_ANALYTICS_BASE_PATH;

/** Whether rearrange mode may drop spaces into this sidebar group (cross-group moves and empty-group targets). */
export function groupAcceptsSpaceDrops(group: NavigationItem): boolean {
  if (group.allowSpaceDrops === false) {
    return false;
  }
  if (group.allowSpaceDrops === true) {
    return true;
  }
  return (group.children ?? []).some((c: NavigationItem) => c.isEditable);
}

function canAccessOperationsContent(isAuthorized: (permission: ACTION) => boolean) {
  return isAuthorized(ACTION.VIEW_PORTFOLIO) || isAuthorized(ACTION.EDIT_OPERATIONS);
}

function canAccessRebalance(isAuthorized: (permission: ACTION) => boolean) {
  return isAuthorized(ACTION.EDIT_REBALANCE);
}

export function canAccessPortfolioNav(
  isAuthorized: (permission: ACTION) => boolean,
  enablePortfolioManagementSystem: boolean,
  enablePMSPENavigationMigration: boolean
) {
  if (enablePortfolioManagementSystem && !enablePMSPENavigationMigration) {
    return isAuthorized(ACTION.VIEW_PORTFOLIO);
  }
  return isAuthorized(ACTION.VIEW_PORTFOLIO) || canAccessRebalance(isAuthorized);
}

function canAccessAdvancedPortfolioJobs(isAuthorized: (permission: ACTION) => boolean) {
  return (
    isAuthorized(ACTION.EDIT_RESEARCH) || isAuthorized(ACTION.EDIT_REBALANCE) || isAuthorized(ACTION.EDIT_OPERATIONS)
  );
}

function canAccessAdvancedPortfolioPermissions(isAuthorized: (permission: ACTION) => boolean) {
  return isAuthorized(ACTION.EDIT_USERS) && canAccessAdvancedPortfolioJobs(isAuthorized);
}

function canAccessLegacyPortfolioEngineering(isAuthorized: (permission: ACTION) => boolean) {
  return isAuthorized(ACTION.EDIT_PORTFOLIO_ENGINEERING);
}

/** Bookmarks under `/portfolio-engineering/*` still redirect after the PE role is removed. */
export function canAccessLegacyPortfolioEngineeringRedirect(isAuthorized: (permission: ACTION) => boolean) {
  return canAccessRebalance(isAuthorized) || isAuthorized(ACTION.EDIT_RESEARCH) || isAuthorized(ACTION.EDIT_OPERATIONS);
}

/** Dealers with Counterparty Monitoring flags can open Operations without PMS Full. */
export function canAccessOperationsAsDealer(
  flags: FeatureFlags,
  isAuthorized: (permission: ACTION) => boolean
): boolean {
  return isAuthorized(ACTION.VIEW_DEALER) && flags.viewDealerMarginPages && flags.viewCounterpartyMonitoring;
}

/**
 * All leaf node paths organized by section.
 * These are the actual routable paths that have components.
 * Add new paths here and they'll automatically be typed.
 */
export const PATHS = {
  trading: {
    analytics: {
      tca: TRADING_ANALYTICS_BASE_PATH,
    },
  },
  portfolio: {
    positions: {
      overview: '/portfolio/positions/overview',
      performance: '/portfolio/positions/performance',
      margin: '/portfolio/positions/margin',
      transactions: '/portfolio/positions/transactions',
      reports: '/portfolio/positions/reports',
    },
    risk: {
      exposures: '/portfolio/risk/exposures',
    },
    portfolioConstruction: {
      rebalancer: '/portfolio/portfolio-construction/rebalancer',
      attribution: '/portfolio/portfolio-construction/attribution',
      models: '/portfolio/portfolio-construction/models',
    },
    dataManagement: {
      benchmarks: '/portfolio/data-management/benchmarks',
      factorModels: '/portfolio/data-management/factor-models',
      timeSeries: '/portfolio/data-management/time-series',
      universes: '/portfolio/data-management/universes',
    },
  },
  research: {
    tools: {
      backTester: '/research/tools/back-tester',
      models: '/research/tools/models',
    },
    dataManagement: {
      benchmarks: '/research/data-management/benchmarks',
      factorModels: '/research/data-management/factor-models',
      timeSeries: '/research/data-management/time-series',
      universes: '/research/data-management/universes',
    },
  },
  settings: {
    general: '/settings/general',
    trading: '/settings/trading',
    apiKey: '/settings/api-key',
    userNotifications: '/settings/user-notifications',
  },
  admin: {
    subAccounts: '/admin/sub-accounts',
    allocationControls: '/admin/allocation-controls',
    users: '/admin/users',
    userGroups: '/admin/user-groups',
    serviceAccounts: '/admin/service-accounts',
    trading: {
      tradingAggregations: '/admin/trading/trading-aggregations',
      tradingControls: '/admin/trading/trading-controls',
      tradingLimits: '/admin/trading/trading-limits',
      unifiedLiquidity: '/admin/trading/unified-liquidity',
      crossAssetRiskGroups: '/admin/trading/cross-asset-risk-groups',
    },
    notifications: {
      destinations: '/admin/notifications/destinations',
      ruleManagement: '/admin/notifications/rule-management',
    },
    connectivity: {
      addresses: '/admin/connectivity/addresses',
      counterparties: '/admin/connectivity/counterparties',
      credentials: '/admin/connectivity/credentials',
      marketAccounts: '/admin/connectivity/market-accounts',
    },
    securityMaster: {
      securityMaster: '/admin/security-master/security-master',
      multilegSecurityMaster: '/admin/security-master/multileg-security-master',
    },
    predictionMarkets: {
      eventSeries: '/admin/prediction-markets/event-series',
      events: '/admin/prediction-markets/events',
      resolutions: '/admin/prediction-markets/resolutions',
    },
    advancedPortfolio: {
      jobs: '/admin/advanced-portfolio/jobs',
      permissions: '/admin/advanced-portfolio/permissions',
    },
  },
  operations: {
    treasuryManagement: {
      overview: '/operations/treasury-management/overview',
      transactions: '/operations/treasury-management/transactions',
      lpSettlementLegacy: '/operations/treasury-management/lp-settlement-legacy',
      lpSettlement: '/operations/treasury-management/lp-settlement',
      operationalReports: '/operations/treasury-management/operational-reports',
    },
    operationalRisk: {
      counterpartyMonitoring: '/operations/operational-risk/counterparty-monitoring',
      reconciliation: '/operations/operational-risk/reconciliation',
    },
    compliance: {
      approvalsAndPolicies: '/operations/compliance/approvals-and-policies',
      universes: '/operations/compliance/universes',
    },
  },
  dealer: {
    monitoring: '/dealer/monitoring',
    configurations: '/dealer/configurations',
    symbolSetup: {
      secmaster: '/dealer/symbol-setup/secmaster',
      symbolGroups: '/dealer/symbol-setup/symbol-groups',
    },
    pricing: {
      tiers: '/dealer/pricing/tiers',
      pricingRulesLegacy: '/dealer/pricing/pricing-rules-legacy',
      pricingRulesBeta: '/dealer/pricing/pricing-rules',
      subAccountMappings: '/dealer/pricing/sub-account-mappings',
      aggregations: '/dealer/pricing/aggregations',
    },
    execution: {
      orderAcceptanceRules: '/dealer/execution/order-acceptance-rules',
      rfqProtocolRules: '/dealer/execution/rfq-protocol-rules',
    },
    buyingPower: {
      balances: '/dealer/buying-power/balances',
      transfers: '/dealer/buying-power/transfers',
      credit: '/dealer/buying-power/credit',
      addresses: '/dealer/buying-power/addresses',
      settlement: '/dealer/buying-power/settlement',
    },
    netAcquisition: {
      limits: '/dealer/net-acquisition/limits',
      memberships: '/dealer/net-acquisition/memberships',
    },
    admin: {
      users: '/dealer/admin/users',
      customerLimits: '/dealer/admin/customer-limits',
      customerAccounts: '/dealer/admin/customer-accounts',
      customerAccountConfigurations: '/dealer/admin/customer-account-configurations',
      customerMargin: '/dealer/admin/customer-margin',
      fixConnections: '/dealer/admin/fix-connections',
    },
  },
  talosAdmin: {
    setup: {
      configurations: '/talos-admin/setup/configurations',
      marketAccountsDeprecated: '/talos-admin/setup/market-accounts-deprecated',
      organizations: '/talos-admin/setup/organizations',
      organizationsDeprecated: '/talos-admin/setup/organizations-deprecated',
      users: '/talos-admin/setup/users',
      orderStrategies: '/talos-admin/setup/order-strategies',
    },
    ops: {
      entityAdminLoadTesting: '/talos-admin/ops/entityadmin-load-testing',
      entityAdminWorkflows: '/talos-admin/ops/entityadmin-workflows',
      fixConnections: '/talos-admin/ops/fix-connections',
      marketConnections: '/talos-admin/ops/market-connections',
      processes: '/talos-admin/ops/processes',
      productTemplates: '/talos-admin/ops/product-templates',
    },
    globalSecMaster: {
      currencies: '/talos-admin/global-sec-master/currencies',
      securities: '/talos-admin/global-sec-master/securities',
    },
  },
} as const;

// ============================================================================
// Path Type Utilities
// ============================================================================

/** Recursively extracts all string values from a nested object */
type DeepValues<T> = T extends string ? T : T extends object ? { [K in keyof T]: DeepValues<T[K]> }[keyof T] : never;

export type LegacyPortfolioEngineeringPath =
  | '/portfolio-engineering/manage/rebalance'
  | '/portfolio-engineering/manage/compliance'
  | '/portfolio-engineering/manage/reporting'
  | '/portfolio-engineering/research/back-tester'
  | '/portfolio-engineering/research/models'
  | '/portfolio-engineering/entities/benchmarks'
  | '/portfolio-engineering/entities/risk-models'
  | '/portfolio-engineering/entities/ts'
  | '/portfolio-engineering/entities/universes'
  | '/portfolio-engineering/setup/jobs'
  | '/portfolio-engineering/setup/iam';

/** All leaf paths in the Portfolio section */
export type PortfolioPath = DeepValues<typeof PATHS.portfolio>;

/** All leaf paths in the Research section */
export type ResearchPath = DeepValues<typeof PATHS.research>;

/** All fixed leaf paths in the Trading section */
export type TradingPath = DeepValues<typeof PATHS.trading>;

/** All leaf paths in the Settings section */
export type SettingsPath = DeepValues<typeof PATHS.settings>;

/** All leaf paths in the Admin section */
export type AdminPath = DeepValues<typeof PATHS.admin>;

/** All leaf paths in the Dealer section */
export type DealerPath = DeepValues<typeof PATHS.dealer>;

/** All leaf paths in the Operations section */
export type OperationsPath = DeepValues<typeof PATHS.operations>;

/** All leaf paths in the TalosAdmin section */
export type TalosAdminPath = DeepValues<typeof PATHS.talosAdmin>;

/**
 * Legacy Portfolio navigation structure for PMS Full (enablePMSFull = true).
 */
export const PORTFOLIO_LEGACY_FULL_NAV: NavigationItem[] = [
  {
    path: '/portfolio/portfolio-management',
    label: 'Portfolio Management',
    children: [
      {
        path: '/portfolio/portfolio-management/overview',
        label: 'Overview',
        mixpanelEvent: MixpanelEvent.NavigateToPortfolioManagementOverview,
      },
      {
        path: '/portfolio/portfolio-management/performance',
        label: 'Performance',
        mixpanelEvent: MixpanelEvent.NavigateToPortfolioManagementPerformance,
      },
      {
        path: '/portfolio/portfolio-management/margin',
        label: 'Margin',
        isVisible: (flags: FeatureFlags) => flags.enableMarginManagementFeatures === true,
        mixpanelEvent: MixpanelEvent.NavigateToPortfolioManagementMargin,
      },
      {
        path: '/portfolio/portfolio-management/transactions',
        label: 'Transactions',
        mixpanelEvent: MixpanelEvent.NavigateToPortfolioManagementTransactions,
      },
      {
        path: '/portfolio/portfolio-management/reports',
        label: 'Reports',
        mixpanelEvent: MixpanelEvent.NavigateToPortfolioManagementReports,
        children: [
          {
            // Structural catch-all so matchRoutes keeps the portfolio submenu open on /run|/saved.
            path: '/portfolio/portfolio-management/reports/*',
            label: undefined,
          },
        ],
      },
    ],
  },
  {
    path: '/portfolio/risk-management',
    label: 'Risk Management',
    children: [
      {
        path: '/portfolio/risk-management/exposures',
        label: 'Risk Exposures',
        mixpanelEvent: MixpanelEvent.NavigateToPortfolioRiskExposures,
      },
    ],
  },
  {
    path: '/portfolio/operations',
    label: 'Operations',
    children: [
      {
        path: '/portfolio/operations/overview',
        label: 'Overview',
        mixpanelEvent: MixpanelEvent.NavigateToPortfolioOperationsOverview,
      },
      {
        path: '/portfolio/operations/transactions',
        label: 'Transactions',
        mixpanelEvent: MixpanelEvent.NavigateToPortfolioOperationsTransactions,
      },
      {
        path: '/portfolio/operations/lp-settlement-legacy',
        label: 'Settlement (Legacy)',
        isVisible: flags => isLpSettlementLegacyVisible(flags),
        mixpanelEvent: MixpanelEvent.NavigateToPortfolioOperationsSettlement,
      },
      {
        path: '/portfolio/operations/lp-settlement',
        label: 'Settlement',
        isVisible: (flags, isAuthorized) =>
          flags.enableLpSettlement === true && isAuthorized(ACTION.VIEW_LP_SETTLEMENT),
        mixpanelEvent: MixpanelEvent.NavigateToPortfolioLpSettlement,
        children: [
          {
            path: '/portfolio/operations/lp-settlement/unsettled-transactions',
            label: 'Unsettled Transactions',
          },
          {
            path: '/portfolio/operations/lp-settlement/active-batches',
            label: 'Active Batches',
          },
          {
            path: '/portfolio/operations/lp-settlement/batch-history',
            label: 'Batch History',
          },
        ],
      },
      {
        path: '/portfolio/operations/reconciliation',
        label: 'Reconciliation',
        mixpanelEvent: MixpanelEvent.NavigateToPortfolioOperationsReconciliation,
      },
      {
        path: '/portfolio/operations/reports',
        label: 'Reports',
        mixpanelEvent: MixpanelEvent.NavigateToPortfolioOperationsReports,
        children: [
          {
            path: '/portfolio/operations/reports/*',
            label: undefined,
          },
        ],
      },
    ],
  },
];

/**
 * Portfolio navigation structure for PMS Lite (enablePMSFull = false)
 */
export const PORTFOLIO_LITE_NAV: NavigationItem[] = [
  {
    path: '/portfolio/portfolio-management',
    label: 'Portfolio Management',
    children: [
      {
        path: '/portfolio/portfolio-management/reports',
        label: 'Reports',
        isVisible: flags => flags.enablePortfolioManagementReportsForPMSLite === true,
        mixpanelEvent: MixpanelEvent.NavigateToPortfolioManagementReports,
        children: [
          {
            path: '/portfolio/portfolio-management/reports/*',
            label: undefined,
          },
        ],
      },
    ],
  },
  {
    path: '/portfolio/operations',
    label: 'Operations',
    children: [
      {
        path: '/portfolio/operations/transactions',
        label: 'Transactions',
        mixpanelEvent: MixpanelEvent.NavigateToPortfolioOperationsTransactions,
      },
      {
        path: '/portfolio/operations/lp-settlement-legacy',
        label: 'Settlement (Legacy)',
        isVisible: flags => isLpSettlementLegacyVisible(flags),
        mixpanelEvent: MixpanelEvent.NavigateToPortfolioOperationsSettlement,
      },
      {
        path: '/portfolio/operations/lp-settlement',
        label: 'Settlement',
        isVisible: (flags, isAuthorized) =>
          flags.enableLpSettlement === true && isAuthorized(ACTION.VIEW_LP_SETTLEMENT),
        mixpanelEvent: MixpanelEvent.NavigateToPortfolioLpSettlement,
        children: [
          {
            path: '/portfolio/operations/lp-settlement/active-batches',
            label: 'Active Batches',
          },
          {
            path: '/portfolio/operations/lp-settlement/unsettled-transactions',
            label: 'Unsettled Transactions',
          },
          {
            path: '/portfolio/operations/lp-settlement/batch-history',
            label: 'Batch History',
          },
        ],
      },
      {
        path: '/portfolio/operations/reconciliation',
        label: 'Reconciliation',
        isVisible: flags => flags.enablePortfolioSubAccountRecon === true,
        mixpanelEvent: MixpanelEvent.NavigateToPortfolioOperationsReconciliation,
      },
      {
        path: '/portfolio/operations/reports',
        label: 'Reports',
        mixpanelEvent: MixpanelEvent.NavigateToPortfolioOperationsReports,
        children: [
          {
            path: '/portfolio/operations/reports/*',
            label: undefined,
          },
        ],
      },
    ],
  },
];

export const PORTFOLIO_NAV: NavigationItem = {
  path: '/portfolio',
  label: 'Portfolio',
  icon: IconName.Portfolio,
  isVisible: (flags, isAuthorized) =>
    canAccessPortfolioNav(isAuthorized, flags.enablePortfolioManagementSystem, flags.enablePMSPENavigationMigration),
  position: 'top',
  children: [
    {
      path: '/portfolio/positions',
      label: 'Positions',
      isVisible: (flags, isAuthorized) => isAuthorized(ACTION.VIEW_PORTFOLIO),
      children: [
        {
          path: '/portfolio/positions/overview',
          label: 'Positions Overview',
          mixpanelEvent: MixpanelEvent.NavigateToPortfolioPositionsOverview,
        },
        {
          path: '/portfolio/positions/performance',
          label: 'Performance',
          mixpanelEvent: MixpanelEvent.NavigateToPortfolioPositionsPerformance,
        },
        {
          path: '/portfolio/positions/margin',
          label: 'Margin',
          isVisible: (flags: FeatureFlags) => flags.enableMarginManagementFeatures === true,
          mixpanelEvent: MixpanelEvent.NavigateToPortfolioPositionsMargin,
        },
        {
          path: '/portfolio/positions/transactions',
          label: 'Transactions',
          mixpanelEvent: MixpanelEvent.NavigateToPortfolioPositionsTransactions,
        },
        {
          path: '/portfolio/positions/reports',
          label: 'Positions Reports',
          mixpanelEvent: MixpanelEvent.NavigateToPortfolioPositionsReports,
          children: [
            {
              // Structural catch-all so matchRoutes keeps the portfolio submenu open on /run|/saved.
              path: '/portfolio/positions/reports/*',
              label: undefined,
            },
          ],
        },
      ],
    },
    {
      path: '/portfolio/risk',
      label: 'Risk',
      isVisible: (flags, isAuthorized) => isAuthorized(ACTION.VIEW_PORTFOLIO),
      children: [
        {
          path: '/portfolio/risk/exposures',
          label: 'Exposures',
          mixpanelEvent: MixpanelEvent.NavigateToPortfolioRiskExposures,
        },
      ],
    },
    {
      path: '/portfolio/portfolio-construction',
      label: 'Portfolio Construction',
      isVisible: (flags, isAuthorized) => canAccessRebalance(isAuthorized),
      children: [
        {
          path: '/portfolio/portfolio-construction/rebalancer',
          label: 'Rebalancer',
          mixpanelEvent: MixpanelEvent.NavigateToPortfolioPortfolioConstructionRebalance,
        },
        {
          path: '/portfolio/portfolio-construction/attribution',
          label: 'Attribution',
          mixpanelEvent: MixpanelEvent.NavigateToPortfolioPortfolioConstructionAttribution,
        },
        {
          path: '/portfolio/portfolio-construction/models',
          label: 'Models',
          mixpanelEvent: MixpanelEvent.NavigateToPortfolioPortfolioConstructionModels,
        },
      ],
    },
    {
      path: '/portfolio/data-management',
      label: 'Data Management',
      isVisible: (flags, isAuthorized) => canAccessRebalance(isAuthorized),
      children: [
        {
          path: '/portfolio/data-management/benchmarks',
          label: 'Benchmarks',
          mixpanelEvent: MixpanelEvent.NavigateToPortfolioDataManagementBenchmarks,
        },
        {
          path: '/portfolio/data-management/factor-models',
          label: 'Factor Models',
          mixpanelEvent: MixpanelEvent.NavigateToPortfolioDataManagementFactorModels,
        },
        {
          path: '/portfolio/data-management/time-series',
          label: 'Time-Series',
          mixpanelEvent: MixpanelEvent.NavigateToPortfolioDataManagementTimeSeries,
        },
        {
          path: '/portfolio/data-management/universes',
          label: 'Universes',
          mixpanelEvent: MixpanelEvent.NavigateToPortfolioDataManagementUniverses,
        },
      ],
    },
  ],
};

export const RESEARCH_NAV: NavigationItem = {
  path: '/research',
  label: 'Research',
  icon: IconName.Research,
  isVisible: (flags, isAuthorized) => isAuthorized(ACTION.EDIT_RESEARCH),
  position: 'top',
  children: [
    {
      path: '/research/tools',
      label: 'Tools',
      children: [
        {
          path: '/research/tools/back-tester',
          label: 'Back-Tester',
          mixpanelEvent: MixpanelEvent.NavigateToResearchToolsBackTester,
        },
        {
          path: '/research/tools/models',
          label: 'Models',
          mixpanelEvent: MixpanelEvent.NavigateToResearchToolsModels,
        },
      ],
    },
    {
      path: '/research/data-management',
      label: 'Data Management',
      children: [
        {
          path: '/research/data-management/benchmarks',
          label: 'Benchmarks',
          mixpanelEvent: MixpanelEvent.NavigateToResearchDataManagementBenchmarks,
        },
        {
          path: '/research/data-management/factor-models',
          label: 'Factor Models',
          mixpanelEvent: MixpanelEvent.NavigateToResearchDataManagementFactorModels,
        },
        {
          path: '/research/data-management/time-series',
          label: 'Time-Series',
          mixpanelEvent: MixpanelEvent.NavigateToResearchDataManagementTimeSeries,
        },
        {
          path: '/research/data-management/universes',
          label: 'Universes',
          mixpanelEvent: MixpanelEvent.NavigateToResearchDataManagementUniverses,
        },
      ],
    },
  ],
};

export const OPERATIONS_NAV: NavigationItem = {
  path: '/operations',
  label: 'Operations',
  icon: IconName.Operations,
  isVisible: (flags, isAuthorized) =>
    canAccessOperationsContent(isAuthorized) || canAccessOperationsAsDealer(flags, isAuthorized),
  position: 'top',
  children: [
    {
      path: '/operations/treasury-management',
      label: 'Treasury Management',
      children: [
        {
          path: '/operations/treasury-management/overview',
          label: 'Treasury Overview',
          isVisible: (flags, isAuthorized) => canAccessOperationsContent(isAuthorized),
          mixpanelEvent: MixpanelEvent.NavigateToOperationsTreasuryManagementOverview,
        },
        {
          path: '/operations/treasury-management/transactions',
          label: 'Transactions',
          isVisible: (flags, isAuthorized) => canAccessOperationsContent(isAuthorized),
          mixpanelEvent: MixpanelEvent.NavigateToOperationsTreasuryManagementTransactions,
        },
        {
          path: '/operations/treasury-management/lp-settlement-legacy',
          label: 'Settlement (Legacy)',
          isVisible: (flags, isAuthorized) =>
            canAccessOperationsContent(isAuthorized) && isLpSettlementLegacyVisible(flags),
          mixpanelEvent: MixpanelEvent.NavigateToPortfolioOperationsSettlement,
        },
        {
          path: '/operations/treasury-management/lp-settlement',
          label: 'Settlement',
          isVisible: (flags, isAuthorized) =>
            canAccessOperationsContent(isAuthorized) &&
            flags.enableLpSettlement === true &&
            isAuthorized(ACTION.VIEW_LP_SETTLEMENT),
          mixpanelEvent: MixpanelEvent.NavigateToOperationsTreasuryManagementLpSettlement,
          children: [
            {
              path: '/operations/treasury-management/lp-settlement/active-batches',
              label: 'Active Batches',
            },
            {
              path: '/operations/treasury-management/lp-settlement/unsettled-transactions',
              label: 'Unsettled Transactions',
            },
            {
              path: '/operations/treasury-management/lp-settlement/batch-history',
              label: 'Batch History',
            },
          ],
        },
        {
          path: '/operations/treasury-management/operational-reports',
          label: 'Operational Reports',
          isVisible: (flags, isAuthorized) => canAccessOperationsContent(isAuthorized),
          mixpanelEvent: MixpanelEvent.NavigateToOperationsTreasuryManagementOperationalReports,
          children: [
            {
              path: '/operations/treasury-management/operational-reports/*',
              label: undefined,
            },
          ],
        },
      ],
    },
    {
      path: '/operations/operational-risk',
      label: 'Operational Risk',
      children: [
        {
          path: PATHS.operations.operationalRisk.counterpartyMonitoring,
          label: 'Counterparty Monitoring',
          isVisible: (flags, isAuthorized) => canAccessOperationsAsDealer(flags, isAuthorized),
          mixpanelEvent: MixpanelEvent.NavigateToOperationsOperationalRiskCounterpartyMonitoring,
        },
        {
          path: '/operations/operational-risk/reconciliation',
          label: 'Reconciliation',
          isVisible: (flags, isAuthorized) => canAccessOperationsContent(isAuthorized),
          mixpanelEvent: MixpanelEvent.NavigateToOperationsOperationalRiskReconciliation,
        },
      ],
    },
    {
      path: '/operations/compliance',
      label: 'Compliance',
      isVisible: (flags, isAuthorized) => isAuthorized(ACTION.EDIT_OPERATIONS),
      children: [
        {
          path: '/operations/compliance/approvals-and-policies',
          label: 'Approvals & Policies',
          isVisible: (flags, isAuthorized) => isAuthorized(ACTION.EDIT_OPERATIONS),
          mixpanelEvent: MixpanelEvent.NavigateToOperationsComplianceApprovalsAndPolicies,
        },
        {
          path: '/operations/compliance/universes',
          label: 'Universes',
          isVisible: (flags, isAuthorized) => isAuthorized(ACTION.EDIT_OPERATIONS),
          mixpanelEvent: MixpanelEvent.NavigateToOperationsComplianceUniverses,
        },
      ],
    },
  ],
};

export const LEGACY_PORTFOLIO_ENGINEERING_NAV: NavigationItem = {
  path: '/portfolio-engineering',
  label: 'Portfolio Engineering',
  shortLabel: 'PE',
  icon: IconName.PortfolioEngineering,
  isVisible: (_flags, isAuthorized) => canAccessLegacyPortfolioEngineering(isAuthorized),
  position: 'top',
  children: [
    {
      path: '/portfolio-engineering/manage',
      label: 'Manage',
      children: [
        {
          path: '/portfolio-engineering/manage/rebalance',
          label: 'Rebalance',
          mixpanelEvent: MixpanelEvent.NavigateToPortfolioEngineeringRebalance,
        },
        {
          path: '/portfolio-engineering/manage/compliance',
          label: 'Compliance',
          mixpanelEvent: MixpanelEvent.NavigateToPortfolioEngineeringCompliance,
        },
        {
          path: '/portfolio-engineering/manage/reporting',
          label: 'Reporting',
          mixpanelEvent: MixpanelEvent.NavigateToPortfolioEngineeringReporting,
        },
      ],
    },
    {
      path: '/portfolio-engineering/research',
      label: 'Research',
      children: [
        {
          path: '/portfolio-engineering/research/back-tester',
          label: 'Back-tester',
          mixpanelEvent: MixpanelEvent.NavigateToPortfolioEngineeringBackTester,
        },
        {
          path: '/portfolio-engineering/research/models',
          label: 'Models',
          mixpanelEvent: MixpanelEvent.NavigateToPortfolioEngineeringModels,
        },
      ],
    },
    {
      path: '/portfolio-engineering/entities',
      label: 'Entities',
      children: [
        {
          path: '/portfolio-engineering/entities/benchmarks',
          label: 'Benchmarks',
          mixpanelEvent: MixpanelEvent.NavigateToPortfolioEngineeringBenchmarks,
        },
        {
          path: '/portfolio-engineering/entities/risk-models',
          label: 'Risk Models',
          mixpanelEvent: MixpanelEvent.NavigateToPortfolioEngineeringRiskModels,
        },
        {
          path: '/portfolio-engineering/entities/ts',
          label: 'Time-Series',
          mixpanelEvent: MixpanelEvent.NavigateToPortfolioEngineeringTimeSeries,
        },
        {
          path: '/portfolio-engineering/entities/universes',
          label: 'Universes',
          mixpanelEvent: MixpanelEvent.NavigateToPortfolioEngineeringUniverses,
        },
      ],
    },
    {
      path: '/portfolio-engineering/setup',
      label: 'Setup',
      children: [
        {
          path: '/portfolio-engineering/setup/jobs',
          label: 'Jobs',
          mixpanelEvent: MixpanelEvent.NavigateToPortfolioEngineeringJobs,
        },
        {
          path: '/portfolio-engineering/setup/iam',
          label: 'Permissions',
          isVisible: (_flags, isAuthorized) => isAuthorized(ACTION.EDIT_USERS),
          mixpanelEvent: MixpanelEvent.NavigateToPortfolioEngineeringPermissions,
        },
      ],
    },
  ],
};

export const LEGACY_OPERATIONS_NAV: NavigationItem = {
  path: '/operations',
  label: 'Operations',
  icon: IconName.Operations,
  position: 'top',
  children: [
    {
      path: '/operations/operational-risk',
      label: 'Operational Risk',
      children: [
        {
          path: PATHS.operations.operationalRisk.counterpartyMonitoring,
          label: 'Counterparty Monitoring',
          isVisible: (flags, isAuthorized) => canAccessOperationsAsDealer(flags, isAuthorized),
        },
      ],
    },
  ],
};

/**
 * Do not use BASE_NAV_PATHS directly, use the `useNavigationItems()` hook which returns filtered navigation items.
 */
export const BASE_NAV_PATHS: NavigationItem[] = [
  {
    path: '/trading',
    label: 'Trading',
    icon: IconName.Trading,
    position: 'top',
    keepWhenEmpty: true,
    children: [
      {
        path: ANALYTICS_NAV_GROUP_PATH,
        label: 'Analytics',
        isVisible: (flags, isAuthorized) =>
          flags.enableAnalyticsInTrading === true && isAuthorized(ACTION.VIEW_ANALYTICS),
        children: [
          {
            path: TRADING_ANALYTICS_BASE_PATH,
            label: 'Post Trade',
            children: [{ path: `${TRADING_ANALYTICS_BASE_PATH}/*`, label: undefined }],
          },
        ],
      },
    ],
  },
  PORTFOLIO_NAV,
  OPERATIONS_NAV,
  {
    path: '/dealer',
    label: 'Dealer',
    icon: IconName.Dealer,
    isVisible: (flags, isAuthorized) => isAuthorized(ACTION.VIEW_DEALER),
    position: 'top',
    children: [
      {
        path: '/dealer',
        label: undefined,
        children: [
          {
            path: '/dealer/monitoring',
            label: 'Monitoring',
            mixpanelEvent: MixpanelEvent.NavigateToDealerMonitoring,
            children: [
              {
                path: '/dealer/monitoring/*',
                label: undefined,
              },
            ],
          },
          {
            path: '/dealer/configurations',
            label: 'Customers',
            isVisible: (flags, isAuthorized) => isAuthorized(ACTION.VIEW_CUSTOMER_CONFIGURATIONS),
            mixpanelEvent: MixpanelEvent.NavigateToDealerConfigurations,
          },
        ],
      },
      {
        path: '/dealer/symbol-setup',
        label: 'Symbol Setup',
        children: [
          {
            path: '/dealer/symbol-setup/secmaster',
            label: 'Security Master',
            mixpanelEvent: MixpanelEvent.NavigateToDealerSecurityMaster,
          },
          {
            path: '/dealer/symbol-setup/symbol-groups',
            label: 'Symbol Groups',
            isVisible: (flags, isAuthorized) =>
              flags.showCustomerTiering === true && isAuthorized(ACTION.VIEW_CUSTOMER_SYMBOL_GROUPS),
            mixpanelEvent: MixpanelEvent.NavigateToDealerSymbolGroups,
          },
        ],
      },
      {
        path: '/dealer/pricing',
        label: 'Pricing',
        children: [
          {
            path: '/dealer/pricing/tiers',
            label: 'Pricing Tiers',
            isVisible: (flags, isAuthorized) =>
              flags.showCustomerTiering === true && isAuthorized(ACTION.VIEW_CUSTOMER_PRICING_TIERS),
            mixpanelEvent: MixpanelEvent.NavigateToDealerPricingTiers,
          },
          {
            path: '/dealer/pricing/pricing-rules-legacy',
            label: 'Pricing Rules (Legacy)',
            isVisible: flags =>
              flags.showCustomerPricingRulesLegacy === true && !flags.requireCustomerPricingRulesCounterpartyFilter,
            mixpanelEvent: MixpanelEvent.NavigateToDealerPricingRulesLegacy,
          },
          {
            path: '/dealer/pricing/pricing-rules',
            label: 'Pricing Rules',
            isVisible: flags => flags.showCustomerPricingRulesBeta === true,
            mixpanelEvent: MixpanelEvent.NavigateToDealerPricingRulesBeta,
          },
          {
            path: '/dealer/pricing/sub-account-mappings',
            label: 'Sub Account Mappings',
            mixpanelEvent: MixpanelEvent.NavigateToDealerSubAccountMappings,
          },
          {
            path: '/dealer/pricing/aggregations',
            label: 'Aggregations',
            mixpanelEvent: MixpanelEvent.NavigateToDealerAggregations,
          },
        ],
      },
      {
        path: '/dealer/execution',
        label: 'Execution',
        children: [
          {
            path: '/dealer/execution/order-acceptance-rules',
            label: 'Order Acceptance Rules',
            isVisible: flags => flags.showCustomerOrderAcceptanceRules === true,
            mixpanelEvent: MixpanelEvent.NavigateToDealerOrderAcceptanceRules,
          },
          {
            path: '/dealer/execution/rfq-protocol-rules',
            label: 'RFQ Protocol Rules',
            isVisible: flags => flags.showCustomerRFQProtocolRules === true,
            mixpanelEvent: MixpanelEvent.NavigateToDealerRFQProtocolRules,
          },
        ],
      },
      {
        path: '/dealer/buying-power',
        label: 'Buying Power',
        isVisible: flags => flags.viewDealerAdminAndBuyingPowerMenuItems === true,
        children: [
          {
            path: '/dealer/buying-power/balances',
            label: 'Customer Balances',
            mixpanelEvent: MixpanelEvent.NavigateToDealerBuyingPowerBalances,
          },
          {
            path: '/dealer/buying-power/transfers',
            label: 'Customer Transfers',
            mixpanelEvent: MixpanelEvent.NavigateToDealerBuyingPowerTransfers,
          },
          {
            path: '/dealer/buying-power/credit',
            label: 'Customer Credit',
            mixpanelEvent: MixpanelEvent.NavigateToDealerBuyingPowerCredit,
          },
          {
            path: '/dealer/buying-power/addresses',
            label: 'Customer Addresses',
            mixpanelEvent: MixpanelEvent.NavigateToDealerBuyingPowerAddresses,
          },
          {
            path: '/dealer/buying-power/settlement',
            label: 'Customer Settlement',
            isVisible: flags => flags.enablePortfolioSettlement === true,
            mixpanelEvent: MixpanelEvent.NavigateToDealerBuyingPowerCustomerSettlement,
          },
        ],
      },
      {
        path: '/dealer/net-acquisition',
        label: 'Net Acquisition',
        isVisible: flags => flags.showCustomerNetAcquisition === true,
        children: [
          {
            path: '/dealer/net-acquisition/limits',
            label: 'Limits',
            isVisible: (flags, isAuthorized) => isAuthorized(ACTION.VIEW_CUSTOMER_ACQUISITION_LIMITS),
            mixpanelEvent: MixpanelEvent.NavigateToDealerNetAcquisitionLimits,
          },
          {
            path: '/dealer/net-acquisition/memberships',
            label: 'Limit Memberships',
            isVisible: (flags, isAuthorized) => isAuthorized(ACTION.VIEW_CUSTOMER_ACQUISITION_LIMITS),
            mixpanelEvent: MixpanelEvent.NavigateToDealerNetAcquisitionLimitMemberships,
          },
        ],
      },
      {
        path: '/dealer/admin',
        label: 'Admin',
        isVisible: flags => flags.viewDealerAdminAndBuyingPowerMenuItems === true,
        children: [
          {
            path: '/dealer/admin/users',
            label: 'Users',
            mixpanelEvent: MixpanelEvent.NavigateToDealerAdminUsers,
          },
          {
            path: '/dealer/admin/customer-limits',
            label: 'Trading Limits',
            mixpanelEvent: MixpanelEvent.NavigateToDealerAdminTradingLimits,
          },
          {
            path: '/dealer/admin/customer-accounts',
            label: 'Customer Accounts',
            mixpanelEvent: MixpanelEvent.NavigateToDealerAdminCustomerAccounts,
          },
          {
            path: '/dealer/admin/customer-account-configurations',
            label: 'Account Configurations',
            mixpanelEvent: MixpanelEvent.NavigateToDealerAdminCustomerAccountConfigurations,
          },
          {
            path: '/dealer/admin/customer-margin',
            label: 'Customer Margin',
            isVisible: flags => flags.viewDealerMarginPages === true,
            mixpanelEvent: MixpanelEvent.NavigateToDealerAdminCustomerMargin,
          },
          {
            path: '/dealer/admin/fix-connections',
            label: 'FIX Connections',
            isVisible: flags => flags.showCustomerFIXConnections === true,
            mixpanelEvent: MixpanelEvent.NavigateToDealerAdminFIXConnections,
          },
        ],
      },
    ],
  },
  {
    path: '/intel',
    label: 'Intel',
    icon: IconName.Intel,
    position: 'top',
    isVisible: (flags, isAuthorized) => flags.enableSignals === true && isAuthorized(ACTION.VIEW_INTEL),
  },
  {
    path: LEGACY_ANALYTICS_BASE_PATH,
    label: 'Analytics',
    icon: IconName.Analytics,
    isVisible: (flags, isAuthorized) => flags.enableAnalyticsInTrading !== true && isAuthorized(ACTION.VIEW_ANALYTICS),
    position: 'top',
  },
  RESEARCH_NAV,
  {
    path: '/monitoring',
    label: 'Monitoring',
    shortLabel: 'Monitor',
    icon: IconName.Monitoring,
    position: 'top',
    isVisible: (flags, isAuthorized) => flags.enableMonitoringBlotters === true,
    keepWhenEmpty: true,
  },
  {
    path: '/admin',
    label: 'Admin',
    icon: IconName.PanelCog,
    position: 'top',
    children: [
      {
        path: '/admin',
        label: undefined,
        children: [
          {
            path: '/admin/sub-accounts',
            label: 'Sub Accounts',
            mixpanelEvent: MixpanelEvent.NavigateToAdminSubAccounts,
          },
          {
            path: '/admin/allocation-controls',
            label: 'Allocation Controls',
            isVisible: flags => flags.enableAllocationControlsPage === true,
            mixpanelEvent: MixpanelEvent.NavigateToAdminAllocationControls,
          },
          {
            path: '/admin/users',
            label: 'Users',
            isVisible: (flags, isAuthorized) => isAuthorized(ACTION.EDIT_FILTER_PERMISSIONS),
            mixpanelEvent: MixpanelEvent.NavigateToAdminUsers,
          },
          {
            path: '/admin/user-groups',
            label: 'User Groups',
            isVisible: (_flags, isAuthorized) => isAuthorized(ACTION.VIEW_USER_GROUPS),
            mixpanelEvent: MixpanelEvent.NavigateToAdminUserGroups,
          },
          {
            path: '/admin/service-accounts',
            label: 'Service Accounts',
            isVisible: (flags, isAuthorized) =>
              flags.enableServiceAccounts === true && isAuthorized(ACTION.VIEW_SERVICE_ACCOUNTS),
            mixpanelEvent: MixpanelEvent.NavigateToAdminServiceAccounts,
          },
        ],
      },
      {
        path: '/admin/trading',
        label: 'Trading',
        children: [
          {
            path: '/admin/trading/trading-aggregations',
            label: 'Trading Aggregations',
            isVisible: flags => flags.showTradingAggregations === true,
            mixpanelEvent: MixpanelEvent.NavigateToAdminTradingTradingAggregations,
          },
          {
            path: '/admin/trading/trading-controls',
            label: 'Trading Controls',
            isVisible: (flags, isAuthorized) =>
              flags.enableTradingControls === true && isAuthorized(ACTION.VIEW_TRADING_CONTROLS),
            mixpanelEvent: MixpanelEvent.NavigateToAdminTradingTradingControls,
          },
          {
            path: '/admin/trading/trading-limits',
            label: 'Trading Limits',
            isVisible: (flags, isAuthorized) => isAuthorized(ACTION.EDIT_LIMITS),
            mixpanelEvent: MixpanelEvent.NavigateToAdminTradingTradingLimits,
          },
          {
            path: '/admin/trading/unified-liquidity',
            label: 'Unified Liquidity',
            isVisible: (flags, isAuthorized) =>
              flags.enableUnifiedLiquiditySelfServe === true && isAuthorized(ACTION.VIEW_UNIFIED_LIQUIDITY_TOKENS),
            mixpanelEvent: MixpanelEvent.NavigateToAdminTradingUnifiedLiquidity,
          },
          {
            path: '/admin/trading/cross-asset-risk-groups',
            label: 'Cross-Asset Risk Groups',
            isVisible: (flags, isAuthorized) =>
              flags.enableCrossAssetRiskGroupPositionLimits === true && isAuthorized(ACTION.EDIT_LIMITS),
          },
        ],
      },
      {
        path: '/admin/advanced-portfolio',
        label: 'Advanced Portfolio',
        isVisible: (flags, isAuthorized) => flags.enablePMSPENavigationMigration === true,
        children: [
          {
            path: PATHS.admin.advancedPortfolio.jobs,
            label: 'Jobs',
            isVisible: (_flags, isAuthorized) => canAccessAdvancedPortfolioJobs(isAuthorized),
            mixpanelEvent: MixpanelEvent.NavigateToPortfolioEngineeringJobs,
          },
          {
            path: PATHS.admin.advancedPortfolio.permissions,
            label: 'Permissions',
            isVisible: (_flags, isAuthorized) => canAccessAdvancedPortfolioPermissions(isAuthorized),
            mixpanelEvent: MixpanelEvent.NavigateToPortfolioEngineeringPermissions,
          },
        ],
      },
      {
        path: '/admin/notifications',
        label: 'Notifications',
        children: [
          {
            path: '/admin/notifications/rule-management',
            label: 'Rule Management',
            isVisible: (flags, isAuthorized) =>
              flags.enableNotifications === true && isAuthorized(ACTION.EDIT_NOTIFICATIONS_ADMIN),
            mixpanelEvent: MixpanelEvent.NavigateToAdminNotificationsRuleManagement,
          },
          {
            path: '/admin/notifications/destinations',
            label: 'Destinations',
            isVisible: (flags, isAuthorized) =>
              flags.enableNotificationsDestinationsAdmin === true &&
              flags.enableNotifications === true &&
              isAuthorized(ACTION.EDIT_NOTIFICATIONS_ADMIN),
            mixpanelEvent: MixpanelEvent.NavigateToAdminNotificationsDestinations,
          },
        ],
      },
      {
        path: '/admin/connectivity',
        label: 'Connectivity',
        children: [
          {
            path: '/admin/connectivity/addresses',
            label: 'Addresses',
            isVisible: (flags, isAuthorized) => isAuthorized(ACTION.VIEW_ADDRESSES),
            mixpanelEvent: MixpanelEvent.NavigateToAdminConnectivityAddresses,
          },
          {
            path: '/admin/connectivity/counterparties',
            label: 'Counterparties',
            isVisible: (flags, isAuthorized) =>
              (flags.enableOTCSupport === true || flags.enableLpSettlement === true) &&
              isAuthorized(ACTION.EDIT_COUNTERPARTIES),
            mixpanelEvent: MixpanelEvent.NavigateToAdminConnectivityCounterparties,
          },
          {
            path: '/admin/connectivity/credentials',
            label: 'Credentials',
            isVisible: (flags, isAuthorized) => isAuthorized(ACTION.VIEW_CREDENTIALS),
            mixpanelEvent: MixpanelEvent.NavigateToAdminConnectivityCredentials,
          },
          {
            path: '/admin/connectivity/market-accounts',
            label: 'Market Accounts',
            isVisible: (flags, isAuthorized) => isAuthorized(ACTION.EDIT_MARKET_ACCOUNTS),
            mixpanelEvent: MixpanelEvent.NavigateToAdminConnectivityMarketAccounts,
          },
        ],
      },
      {
        path: '/admin/security-master',
        label: 'Security Master',
        isVisible: flags => flags.canViewSecurityMaster === true,
        children: [
          {
            path: '/admin/security-master/security-master',
            label: 'Security Master',
            mixpanelEvent: MixpanelEvent.NavigateToAdminSecurityMasterSecurityMaster,
          },
          {
            path: '/admin/security-master/multileg-security-master',
            label: 'Multileg Security Master',
            isVisible: flags => flags.showMultiLegSecmaster === true,
            mixpanelEvent: MixpanelEvent.NavigateToAdminSecurityMasterMultileg,
          },
        ],
      },
      {
        path: '/admin/prediction-markets',
        label: 'Prediction Markets',
        isVisible: flags => flags.enablePredictionMarkets === true,
        children: [
          {
            path: '/admin/prediction-markets/event-series',
            label: 'Event Series',
          },
          {
            path: '/admin/prediction-markets/events',
            label: 'Events',
          },
          {
            path: '/admin/prediction-markets/resolutions',
            label: 'Resolutions',
          },
        ],
      },
    ],
  },
  {
    path: '/talos-admin',
    label: 'Talos Admin',
    icon: IconName.ShieldCheck,
    isVisible: (flags, isAuthorized) => isAuthorized(ACTION.VIEW_TALOS_ADMIN_SETTINGS),
    position: 'top',
    children: [
      {
        path: '/talos-admin/setup',
        label: 'Setup',
        children: [
          {
            path: PATHS.talosAdmin.setup.configurations,
            label: 'Configurations',
            mixpanelEvent: MixpanelEvent.NavigateToTalosAdminConfigurations,
          },
          {
            path: PATHS.talosAdmin.setup.marketAccountsDeprecated,
            label: 'Market Accounts (deprecated)',
            isVisible: flags => flags.showDeprecatedAdminTools === true,
            mixpanelEvent: MixpanelEvent.NavigateToTalosAdminMarketAccountsDeprecated,
          },
          {
            path: PATHS.talosAdmin.setup.organizations,
            label: 'Organizations',
            mixpanelEvent: MixpanelEvent.NavigateToTalosAdminOrganizations,
          },
          {
            path: PATHS.talosAdmin.setup.organizationsDeprecated,
            label: 'Organizations (deprecated)',
            isVisible: flags => flags.showDeprecatedAdminTools === true,
            mixpanelEvent: MixpanelEvent.NavigateToTalosAdminOrganizationsDeprecated,
          },
          {
            path: PATHS.talosAdmin.setup.users,
            label: 'Users',
            mixpanelEvent: MixpanelEvent.NavigateToTalosAdminUsers,
          },
          {
            path: PATHS.talosAdmin.setup.orderStrategies,
            label: 'Order Strategies',
            mixpanelEvent: MixpanelEvent.NavigateToTalosAdminOrderStrategies,
          },
        ],
      },
      {
        path: '/talos-admin/ops',
        label: 'Operations',
        children: [
          {
            path: PATHS.talosAdmin.ops.entityAdminLoadTesting,
            label: 'EntityAdmin Load Testing',
            mixpanelEvent: MixpanelEvent.NavigateToTalosAdminEntityAdminLoadTesting,
          },
          {
            path: PATHS.talosAdmin.ops.entityAdminWorkflows,
            label: 'EntityAdmin Workflows',
            mixpanelEvent: MixpanelEvent.NavigateToTalosAdminEntityAdminWorkflows,
          },
          {
            path: PATHS.talosAdmin.ops.fixConnections,
            label: 'FIX Connections',
            mixpanelEvent: MixpanelEvent.NavigateToTalosAdminFIXConnections,
          },
          {
            path: PATHS.talosAdmin.ops.marketConnections,
            label: 'Market Connections',
            mixpanelEvent: MixpanelEvent.NavigateToTalosAdminMarketConnections,
          },
          {
            path: PATHS.talosAdmin.ops.processes,
            label: 'Processes',
            mixpanelEvent: MixpanelEvent.NavigateToTalosAdminProcesses,
          },
          {
            path: PATHS.talosAdmin.ops.productTemplates,
            label: 'Product Templates',
            isVisible: (flags, isAuthorized) =>
              flags.enableOTCSupport === true && isAuthorized(ACTION.VIEW_PRODUCT_TEMPLATES_ADMIN),
            mixpanelEvent: MixpanelEvent.NavigateToTalosAdminProductTemplates,
          },
        ],
      },
      {
        path: '/talos-admin/global-sec-master',
        label: 'Global Sec Master',
        children: [
          {
            path: PATHS.talosAdmin.globalSecMaster.currencies,
            label: 'Currencies',
            mixpanelEvent: MixpanelEvent.NavigateToTalosAdminCurrencies,
          },
          {
            path: PATHS.talosAdmin.globalSecMaster.securities,
            label: 'Securities',
            mixpanelEvent: MixpanelEvent.NavigateToTalosAdminSecurities,
          },
        ],
      },
    ],
  },
  {
    path: '/settings',
    label: 'User Settings',
    icon: IconName.UserCog,
    position: 'user-menu',
    children: [
      {
        path: '/settings',
        label: undefined,
        children: [
          {
            path: '/settings/general',
            label: 'General',
            mixpanelEvent: MixpanelEvent.NavigateToSettingsUserSettingsGeneral,
          },
          {
            path: '/settings/trading',
            label: 'Trading',
            mixpanelEvent: MixpanelEvent.NavigateToSettingsUserSettingsTrading,
          },
          {
            path: '/settings/api-key',
            label: 'API Key',
            isVisible: flags => flags.enableUserAPIKey === true,
            mixpanelEvent: MixpanelEvent.NavigateToSettingsUserSettingsAPIKey,
          },
          {
            path: '/settings/user-notifications',
            label: 'User Notifications',
            isVisible: flags => flags.enableNotifications === true,
            mixpanelEvent: MixpanelEvent.NavigateToSettingsUserSettingsUserNotifications,
          },
        ],
      },
    ],
  },
];
